import {
  ErrorResponse,
  InMemoryWebStorage,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { SignInError, SignInFailure } from './signInFailure';
import { safeReturnPath, signInReturnPath } from './signInRedirect';

/**
 * The Microsoft leg of Sign in with Microsoft: work or school accounts from
 * any organisation, and personal accounts (Outlook, Hotmail, Live).
 *
 * The browser signs in to Microsoft with PKCE and keeps only the ID token,
 * which `useSignIn` then offers to Cognito as the answer to a CUSTOM_AUTH
 * challenge. Cognito's trigger Lambda (aws-lambda/cognito-microsoft-sign-in)
 * verifies it; nothing here is trusted.
 */

/** Microsoft sends the browser back here; main.tsx keeps the Cognito AuthProvider off it. */
export const MICROSOFT_CALLBACK_PATH = '/auth/microsoft';

// `common` admits work, school and personal accounts alike.
const MICROSOFT_AUTHORITY = 'https://login.microsoftonline.com/common/v2.0';

const microsoftManager = (clientId: string) =>
  new UserManager({
    authority: MICROSOFT_AUTHORITY,
    client_id: clientId,
    redirect_uri: `${window.location.origin}${MICROSOFT_CALLBACK_PATH}`,
    response_type: 'code',
    scope: 'openid email profile',
    prompt: 'select_account',
    loadUserInfo: false,
    automaticSilentRenew: false,
    // Microsoft's own tokens are only needed for the hand-over; never keep them.
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  });

export const isMicrosoftCallback = () =>
  window.location.pathname === MICROSOFT_CALLBACK_PATH;

export const startMicrosoftSignIn = (clientId: string) =>
  microsoftManager(clientId).signinRedirect({
    state: { returnTo: signInReturnPath() },
  });

export interface MicrosoftIdentity {
  /**
   * The addresses the account goes by, to find its Consolidocs account with:
   * its email, then its sign-in name (UPN) when that differs. Organisations
   * such as banks often have both, on different domains of their own.
   */
  addresses: string[];
  idToken: string;
  /** The page of this app the user started from. */
  returnTo: string;
}

/** Only ever return to a page of this app. */
const returnPath = (state: unknown): string =>
  safeReturnPath((state as { returnTo?: unknown } | undefined)?.returnTo);

/** Maps what Microsoft (or oidc-client-ts) reported to the reason the page explains. */
const microsoftFailure = (error: unknown): SignInFailure => {
  if (error instanceof ErrorResponse) {
    const description = error.error_description ?? '';
    if (/AADSTS(65001|90094|90095)/.test(description)) {
      return 'admin_consent_required';
    }
    if (/AADSTS(700016|50020)/.test(description)) return 'tenant_not_allowed';
    // The organisation only lets users it has assigned to the app sign in.
    if (/AADSTS50105/.test(description)) return 'not_assigned';
    // Conditional Access (device, location, risk) or tenant restrictions.
    if (/AADSTS(53000|53001|53002|53003|530003|500021)/.test(description)) {
      return 'blocked_by_policy';
    }
    // Redirect URI missing, or registered under Web rather than Single-page application.
    if (/AADSTS(50011|9002326|9002327)/.test(description)) {
      return 'sign_in_unavailable';
    }
    if (error.error === 'access_denied') return 'access_denied';
  }
  // The sign-in state is gone: too slow, or a different browser tab/profile.
  if (error instanceof Error && /no matching state/i.test(error.message)) {
    return 'session_expired';
  }
  return 'sign_in_failed';
};

/** Reads the sign-in Microsoft has just sent the browser back from. */
export const finishMicrosoftSignIn = async (
  clientId: string
): Promise<MicrosoftIdentity> => {
  try {
    const microsoft = await microsoftManager(clientId).signinCallback();
    const returnTo = returnPath(microsoft?.state);
    const addresses = [
      microsoft?.profile.email,
      microsoft?.profile.preferred_username,
    ].filter(
      (address, index, all): address is string =>
        typeof address === 'string' &&
        address.includes('@') &&
        all.findIndex(
          (other) =>
            typeof other === 'string' &&
            other.toLowerCase() === address.toLowerCase()
        ) === index
    );
    if (!microsoft?.id_token || addresses.length === 0) {
      throw new SignInError('email_not_verified', returnTo);
    }
    return { addresses, idToken: microsoft.id_token, returnTo };
  } catch (error) {
    if (error instanceof SignInError) throw error;
    console.error('Microsoft sign-in failed:', error);
    const returnTo =
      error instanceof ErrorResponse ? returnPath(error.state) : '/';
    throw new SignInError(microsoftFailure(error), returnTo);
  }
};
